import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Company, type Settlement, type TransactionWithProposals } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date, money } from '../format';
import * as Icon from '../components/Icons';

const today = () => new Date().toISOString().slice(0, 10);

type Side = 'receivables' | 'payables' | 'bank';

export function SettlementsPanel({ company, t, locale }: { company: Company; t: Dictionary; locale: Locale }) {
  const [side, setSide] = useState<Side>('receivables');
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [rows, setRows] = useState<Settlement[] | null>(null);
  /** Obie strony naraz, bo liczby wiodące porównują należności z zobowiązaniami. */
  const [openReceivables, setOpenReceivables] = useState<Settlement[]>([]);
  const [openPayables, setOpenPayables] = useState<Settlement[]>([]);
  const [transactions, setTransactions] = useState<TransactionWithProposals[] | null>(null);
  const [imported, setImported] = useState<string | null>(null);
  const [payment, setPayment] = useState<
    { documentId: string; amount: string; paidOn: string; currency: string; exchangeRate: string } | null
  >(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [receivables, payables] = await Promise.all([
        api.receivables(company.id, true),
        api.payables(company.id, true),
      ]);
      setOpenReceivables(receivables);
      setOpenPayables(payables);

      if (side === 'bank') {
        setTransactions(await api.bankTransactions(company.id));
      } else if (onlyOpen) {
        setRows(side === 'receivables' ? receivables : payables);
      } else {
        setRows(
          side === 'receivables'
            ? await api.receivables(company.id, false)
            : await api.payables(company.id, false),
        );
      }
    } catch (failure) {
      setRows(null);
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, side, onlyOpen]);

  /** Wyciąg MT940 to plik tekstowy — czytamy go w przeglądarce i wysyłamy jako tekst. */
  async function importStatement(file: File) {
    setBusy(true);
    setError(null);
    setImported(null);
    try {
      const result = await api.importBankStatement(company.id, await file.text());
      setImported(
        `${t.settlements.importedCount}: ${result.imported} · ${t.settlements.skippedCount}: ${result.skipped}`,
      );
      setTransactions(result.transactions);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  async function confirmMatch(transactionId: string, documentId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.matchBankTransaction(company.id, transactionId, documentId);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!payment) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        paidOn: payment.paidOn,
        amount: payment.amount,
        // Kurs z dnia zapłaty tylko przy walucie obcej; przy złotówkach serwer go nie pyta
        exchangeRate: payment.currency === 'PLN' ? null : payment.exchangeRate.replace(',', '.') || null,
      };
      if (side === 'receivables') await api.payInvoice(company.id, payment.documentId, body);
      else await api.payCost(company.id, payment.documentId, body);
      setPayment(null);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const editable = !company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER');
  const sum = (list: Settlement[]) => list.reduce((total, item) => total + Number(item.remaining), 0);
  const longest = [...openReceivables, ...openPayables].reduce<Settlement | null>(
    (worst, item) => (item.daysOverdue > (worst?.daysOverdue ?? 0) ? item : worst),
    null,
  );
  const overdueCount = openReceivables.filter((item) => item.daysOverdue > 0).length;

  const needle = query.trim().toLowerCase();
  const shown = (rows ?? []).filter(
    (row) =>
      !needle ||
      [row.documentNumber, row.counterpartyName]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle)),
  );
  const totals = shown.reduce(
    (total, row) => ({
      due: total.due + Number(row.amountDue),
      paid: total.paid + Number(row.amountPaid),
      remaining: total.remaining + Number(row.remaining),
    }),
    { due: 0, paid: 0, remaining: 0 },
  );

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.settlements.title}</h3>
          <p>{t.settlements.lead}</p>
        </div>
        {editable && (
          <div className="sp">
            <button type="button" className="btn sec" onClick={() => setSide('bank')}>
              {t.settlements.loadStatement}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      <div className="split">
        <div className="panel metric">
          <span className="k">{t.settlements.receivablesTotal}</span>
          <span className="v num">{money(sum(openReceivables), locale)}</span>
          <span className="sub">
            {openReceivables.length}
            {overdueCount > 0 && ` · ${t.dashboard.receivablesOverdue}: ${overdueCount}`}
          </span>
        </div>
        <div className="panel metric">
          <span className="k">{t.settlements.payablesTotal}</span>
          <span className="v num">{money(sum(openPayables), locale)}</span>
          <span className="sub">{openPayables.length}</span>
        </div>
        <div className="panel metric">
          <span className="k">{t.settlements.longestDelay}</span>
          <span className="v num">{longest && longest.daysOverdue > 0 ? longest.daysOverdue : '—'}</span>
          <span className="sub">
            {longest && longest.daysOverdue > 0
              ? `${longest.documentNumber ?? '—'} · ${longest.counterpartyName ?? '—'}`
              : t.settlements.noDelay}
          </span>
        </div>
      </div>

      <div className="panel">
        <div className="tabs" role="tablist">
          {(
            [
              ['receivables', `${t.settlements.receivables} · ${openReceivables.length}`],
              ['payables', `${t.settlements.payables} · ${openPayables.length}`],
              ['bank', t.settlements.bank],
            ] as [Side, string][]
          ).map(([key, label]) => (
            <button type="button" role="tab" key={key} aria-selected={side === key} onClick={() => setSide(key)}>
              {label}
            </button>
          ))}
        </div>

        {side === 'bank' ? (
          <>
            <p className="sm muted">{t.settlements.bankHint}</p>
            <div className="row">
              <label className="btn sec" htmlFor="statement">
                {t.settlements.chooseFile}
              </label>
              <input
                id="statement"
                type="file"
                accept=".txt,.sta,.mt940,text/plain"
                disabled={busy}
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void importStatement(file);
                  event.target.value = '';
                }}
              />
              {imported && <span className="sm muted">{imported}</span>}
            </div>

            {transactions?.length === 0 && (
              <div className="empty">
                <b>{t.settlements.noTransactions}</b>
              </div>
            )}
            {transactions && transactions.length > 0 && (
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>{t.settlements.valueDate}</th>
                      <th className="n">{t.settlements.amount}</th>
                      <th>{t.settlements.details}</th>
                      <th>{t.settlements.proposal}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(({ transaction, proposals }) => (
                      <tr key={transaction.id}>
                        <td className="doc">{date(transaction.valueDate, locale)}</td>
                        <td className="n">{amount(transaction.amount, locale)}</td>
                        <td className="muted sm">{transaction.details || '—'}</td>
                        <td>
                          {proposals.length === 0 && <span className="muted sm">{t.settlements.noProposal}</span>}
                          {proposals.map((proposal) => (
                            <div key={proposal.settlement.documentId}>
                              <button
                                type="button"
                                className="btn ghost sm"
                                disabled={busy || !editable}
                                onClick={() => void confirmMatch(transaction.id, proposal.settlement.documentId)}
                              >
                                {proposal.settlement.documentNumber} ·{' '}
                                {amount(proposal.settlement.remaining, locale)}
                              </button>
                              <span className="muted sm"> {t.settlements.reasons[proposal.reason]}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="panel future">
              <div className="hd2">
                <b>{t.settlements.matchFutureTitle}</b>
                <span className="soon">{t.invoices.soon}</span>
              </div>
              <p className="sm muted">{t.settlements.matchFutureText}</p>
            </div>
          </>
        ) : (
          <>
            <div className="row">
              <label className="check">
                <input
                  type="checkbox"
                  checked={onlyOpen}
                  onChange={(event) => setOnlyOpen(event.target.checked)}
                />
                {t.settlements.onlyOpen}
              </label>
              <input
                className="inp"
                style={{ flex: '1 1 200px', maxWidth: 320 }}
                placeholder={t.settlements.filter}
                aria-label={t.settlements.filter}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>

            {shown.length === 0 ? (
              <div className="empty">
                <b>{t.settlements.none}</b>
              </div>
            ) : (
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>{t.settlements.document}</th>
                      <th>{t.settlements.counterparty}</th>
                      <th>{t.settlements.dueDate}</th>
                      {/* Opóźnienie ma własną kolumnę, a nie doklejony tekst w komórce terminu */}
                      <th>{t.settlements.delay}</th>
                      <th className="n">{t.settlements.amountDue}</th>
                      <th className="n">{t.settlements.paid}</th>
                      <th className="n">{t.settlements.remaining}</th>
                      <th>{t.settlements.status}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((row) => (
                      <tr key={row.documentId}>
                        <td className="doc">{row.documentNumber ?? '—'}</td>
                        <td>{row.counterpartyName ?? '—'}</td>
                        <td className="doc">{date(row.dueDate, locale)}</td>
                        <td>
                          {row.daysOverdue > 0 ? (
                            <span className="chip st-bad">
                              <i />
                              {row.daysOverdue}
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="n">{amount(row.amountDue, locale)}</td>
                        <td className="n">{amount(row.amountPaid, locale)}</td>
                        <td className="n">{amount(row.remaining, locale)}</td>
                        <td>
                          <span
                            className={
                              row.status === 'PAID'
                                ? 'chip st-ok'
                                : row.status === 'PARTIAL'
                                  ? 'chip st-warn'
                                  : 'chip st-bad'
                            }
                          >
                            <i />
                            {t.settlements.statuses[row.status]}
                          </span>
                        </td>
                        <td className="act">
                          <div className="row">
                            {editable && row.status !== 'PAID' && (
                              <button
                                type="button"
                                className="btn ghost sm"
                                onClick={() =>
                                  setPayment({
                                    documentId: row.documentId,
                                    amount: row.remaining,
                                    paidOn: today(),
                                    currency: row.currency,
                                    exchangeRate: '',
                                  })
                                }
                              >
                                {t.settlements.addPayment}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4}>
                        {t.invoices.totals} · {shown.length}
                      </td>
                      <td className="n">{amount(totals.due, locale)}</td>
                      <td className="n">{amount(totals.paid, locale)}</td>
                      <td className="n">{amount(totals.remaining, locale)}</td>
                      <td />
                      <td className="act" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {payment && (
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <header>
            <h4>{t.settlements.addPayment}</h4>
          </header>
          <div className="fields">
            <div className="field">
              <label htmlFor="paidOn">{t.settlements.paidOn}</label>
              <input
                id="paidOn"
                className="inp"
                type="date"
                value={payment.paidOn}
                onChange={(event) => setPayment({ ...payment, paidOn: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="paidAmount">
                {t.settlements.amount}
                {payment.currency !== 'PLN' && <span className="muted sm"> {payment.currency}</span>}
              </label>
              <input
                id="paidAmount"
                className="inp num"
                value={payment.amount}
                onChange={(event) => setPayment({ ...payment, amount: event.target.value })}
                inputMode="decimal"
                required
              />
            </div>
            {payment.currency !== 'PLN' && (
              <div className="field">
                <label htmlFor="paidRate">{t.settlements.exchangeRate}</label>
                <input
                  id="paidRate"
                  className="inp num mono"
                  value={payment.exchangeRate}
                  onChange={(event) => setPayment({ ...payment, exchangeRate: event.target.value })}
                  inputMode="decimal"
                  required
                />
                <span className="hint">{t.settlements.exchangeRateHint}</span>
              </div>
            )}
          </div>
          <div className="bar2">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t.companies.saving : t.settlements.savePayment}
            </button>
            <div className="sp">
              <button type="button" className="btn ghost" onClick={() => setPayment(null)} disabled={busy}>
                {t.companies.cancel}
              </button>
            </div>
          </div>
        </form>
      )}
    </>
  );
}
